import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import {
  cartSetCouponErrorsSelector,
  cartStateSelector,
  removeCartCoupon,
  setCartCoupon,
} from '@src/features/cart/store/cart';
import useTranslate, { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import { track } from '@src/services/analytics';

export const useCartCoupon = (initialCode = '') => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const { isLoadingCoupon, cart } = useAppSelector(cartStateSelector);
  const couponErrors = useAppSelector(cartSetCouponErrorsSelector);
  const [couponCode, setCouponCode] = useState(initialCode);

  useEffect(() => {
    if (cart?.coupon?.code) {
      setCouponCode(cart.coupon.code);
    }
  }, [cart?.coupon?.code]);

  const apply = useCallback(
    (code?: string) => {
      const trimmedCode = (code ?? couponCode).trim();

      if (!trimmedCode || cart?.coupon?.code) {
        return;
      }

      track('cartCouponAdded', { couponCode: trimmedCode });
      dispatch(setCartCoupon(trimmedCode));
    },
    [cart?.coupon?.code, couponCode, dispatch],
  );

  const remove = useCallback(() => {
    dispatch(removeCartCoupon());
  }, [dispatch]);

  const errorMessage = couponErrors?.map((key) => i18n(key as DefaultMessagesKeys)).join(', ') ?? '';

  return {
    couponCode,
    setCouponCode,
    apply,
    remove,
    isLoading: isLoadingCoupon,
    hasAppliedCoupon: Boolean(cart?.coupon?.code),
    errorMessage,
    readOnly: Boolean(cart?.coupon?.code),
  };
};

export default useCartCoupon;
